import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import Orb from "@/components/Orb";

const Signup = () => {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", username: "", password: "", display_name: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signup({ ...form, username: form.username.replace(/^@/, "").toLowerCase() });
      toast.success("Account created");
      nav("/timetable?onboarding=1");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white grid md:grid-cols-2">
      <div className="relative hidden md:flex items-center justify-center overflow-hidden border-r border-white/[0.06]">
        <div className="holo-mesh opacity-70" aria-hidden />
        <div className="relative z-10 flex flex-col items-center gap-10 px-10">
          <Orb size={340} />
          <div className="text-center max-w-sm">
            <div className="label-mono mb-3">/ begin</div>
            <div className="font-display text-2xl tightest font-semibold leading-snug">
              Press record once — Vertisiya <span className="chrome-text">handles the rest of the day.</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-6 label-mono">no card required</div>
      </div>

      <div className="relative flex items-center px-6 md:px-16 py-16">
        <div className="w-full max-w-sm mx-auto">
          <div className="flex items-center justify-between mb-16">
            <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 hover:text-white transition-colors text-[0.82rem]" data-testid="back-home">
              <ArrowLeft size={13} /> back
            </Link>
            <Link to="/" className="md:hidden"><Wordmark size="sm" /></Link>
          </div>

          <h1 className="font-display text-4xl tightest font-semibold mb-3 leading-none">Join Vertisiya.</h1>
          <p className="text-neutral-500 text-[0.9rem] mb-10">5 free recordings every month. No card.</p>

          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <div className="label-mono mb-2">Display name</div>
              <input className="input-line" value={form.display_name} onChange={set("display_name")} placeholder="Ada Lovelace" data-testid="signup-name" />
            </div>
            <div>
              <div className="label-mono mb-2">@username</div>
              <input className="input-line" value={form.username} onChange={set("username")} placeholder="ada" required data-testid="signup-username" />
            </div>
            <div>
              <div className="label-mono mb-2">Email</div>
              <input className="input-line" type="email" value={form.email} onChange={set("email")} required placeholder="you@school.edu" data-testid="signup-email" />
            </div>
            <div>
              <div className="label-mono mb-2">Password</div>
              <input className="input-line" type="password" value={form.password} onChange={set("password")} required placeholder="at least 8 characters" data-testid="signup-password" />
            </div>
            <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="signup-submit">
              {busy ? "Creating…" : <>Create account <ArrowRight size={14} /></>}
            </button>
          </form>

          <div className="mt-10 text-[0.85rem] text-neutral-500">
            Already have one?{" "}
            <Link to="/login" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
